import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Nav } from "./nav.tsx";
import { Footer } from "./footer.tsx";
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import i18next from 'i18next';
import '../css/contact.css';

export function ContactUs(props) {
    const [mailSent, setMailSent] = useState<boolean | null>(null); // State for success message
    const [error, setError] = useState<string | null>(null); // State for error message
    const [formData, setFormData] = useState<Record<string, string>>({}); // Form data state

    const { lang } = useParams();
    const { t } = useTranslation();
    const baseURL = "https://www.parlaretail.com";
    useEffect(() => {
        if (lang) {
            i18next.changeLanguage(lang);
        }
    }, [lang]);
    /**
     * Handle form submission
     * @param {Event} e - The form event
     */
    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        axios({
            method: "post",
            url: `${process.env.REACT_APP_API}`,
            headers: { "Content-Type": "application/json" },
            data: formData,
        })
            .then((result) => {
                if (result.status === 200) {
                    setMailSent(true);
                    setError(null);
                } else {
                    setMailSent(false);
                    setError("Failed to send the message. Please try again.");
                }
            })
            .catch((error) => {
                console.error("Error sending form:", error);
                setMailSent(false);
                setError("An error occurred. Please try again.");
            });
    };


    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: string) => {
        setFormData({
            ...formData,
            [field]: e.target.value,
        });
    };

    function closeMsgResponse() {
        setError(null);
        if (mailSent) {
            formData.name = "";
            formData.phone = "";
            formData.email = "";
            formData.job_title = "";
            formData.city = "";
            formData.company_name = "";
            formData.message = "";
            setMailSent(null)
        }
    }

    return (
        <>
            <Helmet key={lang}>
                {/* General Meta */}
                <meta charSet="utf-8" />
                <meta http-equiv="X-UA-Compatible" content="IE=edge" />
                <title>{t('contact_us.meta.title')}</title>
                <meta name="description" content={t('contact_us.meta.description')} />
                <meta name="keywords" content={t('contact_us.meta.keywords')} />
                <meta name="viewport" content="width=device-width, initial-scale=1" />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={t('contact_us.meta.title')} />
                <meta property="og:description" content={t('contact_us.meta.description')} />
                <meta property="og:url" content={`${baseURL}/${lang}/`} />
                <meta property="og:image" content={`${baseURL}/img/sharing-image.jpg`} />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={t('contact_us.meta.title')} />
                <meta name="twitter:description" content={t('contact_us.meta.description')} />
                <meta name="twitter:image" content={`${baseURL}/img/sharing-image.jpg`} />

                {/* Hreflang for Language Versioning */}
                <link rel="alternate" hrefLang="en" href={`${baseURL}/en/`} />
                <link rel="alternate" hrefLang="es" href={`${baseURL}/es/`} />

                {/* Favicon */}
                <link rel="icon" type="image/png" href={`${baseURL}/img/parla-fav.png`} />
            </Helmet>
            <Nav />
            <div className="page">
                <div className="section-column">
                    <div className="row f70 t-center t-color-2 p20">{t('contact_us.title')}</div>
                    <div className="contact-form">
                        <div className="contact-form__container">
                            <form onSubmit={handleFormSubmit}>
                                <div className="input-form-field">
                                    <label>{t('contact_us.form.full_name')}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.name || ""}
                                        onChange={(e) => handleChange(e, "name")}
                                    />
                                </div>
                                <div className="input-form-field">
                                    <label>{t('contact_us.form.phone')}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.phone || ""}
                                        onChange={(e) => handleChange(e, "phone")}
                                    />
                                </div>
                                <div className="input-form-field">
                                    <label>{t('contact_us.form.business_email')}</label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        value={formData.email || ""}
                                        onChange={(e) => handleChange(e, "email")}
                                    />
                                </div>
                                <div className="input-form-field">
                                    <label>{t('contact_us.form.job_title')}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.job_title || ""}
                                        onChange={(e) => handleChange(e, "job_title")}
                                    />
                                </div>
                                <div className="input-form-field">
                                    <label>{t('contact_us.form.city')}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.city || ""}
                                        onChange={(e) => handleChange(e, "city")}
                                    />

                                </div>
                                <div className="input-form-field">
                                    <label>{t('contact_us.form.company_name')}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.company_name || ""}
                                        onChange={(e) => handleChange(e, "company_name")}
                                    />
                                </div>
                                <div className="input-form-textarea">
                                    <label>{t('contact_us.form.message')}</label>
                                    <textarea
                                        className="form-control"
                                        value={formData.message || ""}
                                        onChange={(e) => handleChange(e, "message")}
                                    >
                                    </textarea>
                                </div>

                                <div className="input-form-button">
                                    <input type="submit" value={t('contact_us.form.submit')} />
                                </div>
                                {(mailSent || error) && (
                                    <div className="msg-response">
                                        <div className="msg-response-container">
                                            {mailSent && <div className="success"><i className="far fa-check"></i> Thank you for getting in touch! </div>}
                                            {error && <div className="error"><i className="far fa-exclamation-triangle"></i>Error sending form</div>}
                                            <div className="msg-response-close btn btn-dark" onClick={closeMsgResponse}>Close</div>
                                        </div>
                                    </div>
                                )}
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
}



export default ContactUs;
